import React, { useState, useEffect } from 'react';
import { Container, Form, Button, Alert, Card } from 'react-bootstrap';
import { supabase } from '../lib/supabaseClient';
import './AdminDashboard.css';

const DEFAULT_PHONE = '628132216607';
const DEFAULT_EMAIL = 'edcworkss@gmail.com';
const DEFAULT_INSTAGRAM = 'https://www.instagram.com/edc.workss/';

function AdminContact() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', variant: '' });
  const [formData, setFormData] = useState({
    phone: DEFAULT_PHONE,
    email: DEFAULT_EMAIL,
    instagram: DEFAULT_INSTAGRAM,
  });

  useEffect(() => {
    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('site_settings')
        .select('phone, email, instagram')
        .eq('id', 'main')
        .maybeSingle();

      if (error) throw error;
      if (data) {
        setFormData({
          phone: data.phone || DEFAULT_PHONE,
          email: data.email || DEFAULT_EMAIL,
          instagram: data.instagram || DEFAULT_INSTAGRAM,
        });
      }
    } catch (error) {
      showAlert('Error loading: ' + error.message, 'danger');
      setFormData({
        phone: DEFAULT_PHONE,
        email: DEFAULT_EMAIL,
        instagram: DEFAULT_INSTAGRAM,
      });
    } finally {
      setLoading(false);
    }
  };

  const showAlert = (message, variant = 'success') => {
    setAlert({ show: true, message, variant });
    setTimeout(() => setAlert({ show: false, message: '', variant: '' }), 3000);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const { error } = await supabase
        .from('site_settings')
        .upsert(
          {
            id: 'main',
            phone: formData.phone.trim() || null,
            email: formData.email.trim() || null,
            instagram: formData.instagram.trim() || null,
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'id' }
        );

      if (error) throw error;
      showAlert('Contact info saved successfully!', 'success');
    } catch (error) {
      showAlert('Error saving: ' + error.message, 'danger');
    } finally {
      setSaving(false);
    }
  };

  const formatPhoneDisplay = (p) => {
    if (!p) return '';
    const n = p.replace(/\D/g, '');
    if (n.startsWith('62') && n.length > 2) return '+62 ' + n.slice(2);
    return p;
  };

  if (loading) {
    return (
      <div className="admin-dashboard">
        <Container fluid className="py-4">
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <Container fluid className="py-4">
        <div className="admin-header">
          <div>
            <h1 className="admin-title">Contact Management</h1>
            <p className="admin-subtitle">No HP, Email, dan Instagram untuk Footer & CTA</p>
          </div>
        </div>

        {alert.show && (
          <Alert variant={alert.variant} dismissible onClose={() => setAlert({ show: false })}>
            {alert.message}
          </Alert>
        )}

        <Card className="border-0 shadow-sm">
          <Card.Body className="p-4">
            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-4">
                <Form.Label>
                  <i className="bi bi-telephone-fill me-2 text-primary"></i>
                  No HP / WhatsApp
                </Form.Label>
                <Form.Control
                  type="text"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="628132216607 (tanpa + atau spasi)"
                  className="form-control-lg"
                />
                <Form.Text className="text-muted">
                  Format: 62xxxx (contoh 628132216607). Dipakai untuk link WhatsApp di Footer & CTA.
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label>
                  <i className="bi bi-envelope-fill me-2 text-primary"></i>
                  Email
                </Form.Label>
                <Form.Control
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="edcworkss@gmail.com"
                  className="form-control-lg"
                />
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label>
                  <i className="bi bi-instagram me-2 text-primary"></i>
                  Instagram
                </Form.Label>
                <Form.Control
                  type="url"
                  name="instagram"
                  value={formData.instagram}
                  onChange={handleInputChange}
                  placeholder="https://www.instagram.com/edc.workss/"
                  className="form-control-lg"
                />
                <Form.Text className="text-muted">
                  URL lengkap profil Instagram.
                </Form.Text>
              </Form.Group>

              <Button type="submit" variant="primary" disabled={saving} className="px-4">
                {saving ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="bi bi-check2-circle me-2"></i>
                    Simpan
                  </>
                )}
              </Button>
            </Form>
          </Card.Body>
        </Card>

        <Card className="mt-3 border-0 shadow-sm bg-light">
          <Card.Body className="p-3">
            <small className="text-muted">
              <strong>Preview:</strong> No HP {formatPhoneDisplay(formData.phone) || '-'} | Email {formData.email || '-'} | Instagram {formData.instagram ? (
                <a href={formData.instagram} target="_blank" rel="noopener noreferrer">{formData.instagram}</a>
              ) : '-'}
            </small>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
}

export default AdminContact;
